import { Transform } from "../../../geometry/transformation";
import * as Global from "../../../global";

export class PointMapper {
    private static defMatrix: Mat5 = [[1, 0, 0, 0], [0, -1, 0, 0], [0, 0, 1, 0], [0, 0, 0, 1]]; // the -1 is needed because of flipping the Y coordinate (model has Y facing up, canvas has it facing down)
    public zoom: number = 1;
    public pos: Point2d = { x: 0, y: 0 };
    public worldToScreenTransform: Transform  = new Transform(PointMapper.defMatrix);

    screenToWorld(p: Point2d): Point2d {
        let nx = p.x / this.zoom + this.pos.x;
        let ny = p.y / this.zoom + this.pos.y;
        return { x: nx, y: -ny };
    }

    worldToScreen(p: Point2d): Point2d {
        return this.worldToScreenTransform.point2d(p);
    }
}

//todo: move to a .d.ts file...
export interface DrawOptions {
    color?: string;
    width?: number;
    fillColor?: string;
    lineStyle?: number[];
}

// soul of all renderings from world coordinate system to canvas, be cautios when touching it...
export class EditorCanvas {
    private ctx: CanvasRenderingContext2D | null = null;
    private isRubber: boolean;

    constructor (isRubber: boolean) {
        this.isRubber = isRubber;
    }

    public setContext(context: CanvasRenderingContext2D) {
        this.ctx = context;
    }

    public drawPolyLine(points: Point2d[], strokeOptions: DrawOptions) {
        if (!this.ctx) return;

        this.ctx.strokeStyle = strokeOptions.color ?? Global.DEFAULT_LINE_COLOR;
        this.ctx.lineWidth = (strokeOptions.width ?? Global.DEFAULT_LINE_WIDTH) * (this.isRubber ? 1 : sharedPointMapperRef.current.zoom);
        if (strokeOptions.lineStyle) this.ctx.setLineDash(strokeOptions.lineStyle);

        this.ctx.beginPath();
        const begPoint = sharedPointMapperRef.current.worldToScreen({ x: points[0].x, y: points[0].y });
        this.ctx.moveTo(begPoint.x, begPoint.y);

        for (let i = 1; i < points.length; ++i) {
            const currPoint = sharedPointMapperRef.current.worldToScreen({ x: points[i].x, y: points[i].y });
            this.ctx.lineTo(currPoint.x, currPoint.y);
        }
        this.ctx.stroke();
    }

    public drawPolygon(points: Point2d[], strokeOptions: DrawOptions) {
        if (!this.ctx) return;

        this.ctx.strokeStyle = strokeOptions.color ?? Global.DEFAULT_LINE_COLOR;
        this.ctx.lineWidth = (strokeOptions.width ?? Global.DEFAULT_LINE_WIDTH) * (this.isRubber ? 1 : sharedPointMapperRef.current.zoom);
        if (strokeOptions.lineStyle) this.ctx.setLineDash(strokeOptions.lineStyle);
        if (strokeOptions.fillColor) this.ctx.fillStyle = strokeOptions.fillColor;

        this.ctx.beginPath();
        const begPoint = sharedPointMapperRef.current.worldToScreen({ x: points[0].x, y: points[0].y });
        this.ctx.moveTo(begPoint.x, begPoint.y);

        for (let i = 1; i < points.length; ++i) {
            const currPoint = sharedPointMapperRef.current.worldToScreen({ x: points[i].x, y: points[i].y });
            this.ctx.lineTo(currPoint.x, currPoint.y);
        }
        this.ctx.closePath();
        if (strokeOptions.fillColor) this.ctx.fill();
    }

    public drawCircle(origo: Point2d, radius: number, strokeOptions: DrawOptions, forceNoScale: boolean = false) {
        if (!this.ctx) return;

        const scaleModifier = (forceNoScale) ? 1 : sharedPointMapperRef.current.zoom;

        this.ctx.strokeStyle = strokeOptions.color ?? Global.DEFAULT_LINE_COLOR;
        this.ctx.lineWidth = (strokeOptions.width ?? Global.DEFAULT_LINE_WIDTH) * scaleModifier;
        if (strokeOptions.lineStyle) this.ctx.setLineDash(strokeOptions.lineStyle);
        if (strokeOptions.fillColor) this.ctx.fillStyle = strokeOptions.fillColor;

        this.ctx.beginPath();
        const p = sharedPointMapperRef.current.worldToScreen(origo);
        this.ctx.arc(p.x, p.y, radius * scaleModifier, 0, 2 * Math.PI);
        if (strokeOptions.fillColor) this.ctx.fill();
    }

    public drawArc(origo: Point2d, radius: number, begAngle: number, endAngle: number, clockWise: boolean, strokeOptions: DrawOptions, forceNoScale: boolean = false) {
        if (!this.ctx) return;

        const scaleModifier = (forceNoScale) ? 1 : sharedPointMapperRef.current.zoom;

        this.ctx.strokeStyle = strokeOptions.color ?? Global.DEFAULT_LINE_COLOR;
        this.ctx.lineWidth = (strokeOptions.width ?? Global.DEFAULT_LINE_WIDTH) * scaleModifier;
        if (strokeOptions.lineStyle) this.ctx.setLineDash(strokeOptions.lineStyle);
        if (strokeOptions.fillColor) this.ctx.fillStyle = strokeOptions.fillColor;

        this.ctx.beginPath();
        const p = sharedPointMapperRef.current.worldToScreen(origo);
        this.ctx.arc(p.x, p.y, radius * scaleModifier, -begAngle, -endAngle, !clockWise);
        this.ctx.stroke();
        if (strokeOptions.fillColor) this.ctx.fill();
    }

    public drawImage(pos: Point2d, angle: number, image: CanvasImageSource, width: number, height: number, strokeOptions: DrawOptions, anchor: AnchorType, forceNoScale: boolean = false) {
        if (!this.ctx) return;

        const scaleModifier = (forceNoScale) ? 1 : sharedPointMapperRef.current.zoom;

        const refPoint = sharedPointMapperRef.current.worldToScreen(pos);
        const rot = -angle; // because of canvas origo is top left, and right and down are the positive directions instead of right and up

        let dx: number = 0;
        let dy: number = 0;
        if (anchor.horizontal === "left") dx = 0 * scaleModifier;
        if (anchor.horizontal === "center") dx = width / 2 * scaleModifier;
        if (anchor.horizontal === "right") dx = width * scaleModifier;
        if (anchor.vertical === "top") dy = 0 * scaleModifier;
        if (anchor.vertical === "center") dy = height / 2 * scaleModifier;
        if (anchor.vertical === "bottom") dy = height * scaleModifier;

        this.ctx.translate(refPoint.x, refPoint.y);
        this.ctx.rotate(rot);
        this.ctx.drawImage(image, -dx, -dy, width * scaleModifier, height * scaleModifier);
        this.ctx.rotate(-rot);
        this.ctx.translate(-refPoint.x, -refPoint.y);
    }

    public drawText(pos: Point2d, angle: number, size: number, text: string, strokeOptions: DrawOptions) {
        if (!this.ctx) return;

        this.ctx.strokeStyle = strokeOptions.color ?? Global.DEFAULT_TEXT_COLOR;
        this.ctx.lineWidth = (strokeOptions.width ?? Global.DEFAULT_TEXT_OUTLINE_WIDTH) * sharedPointMapperRef.current.zoom;
        if (strokeOptions.fillColor) this.ctx.fillStyle = strokeOptions.fillColor;

        this.ctx.font = size * sharedPointMapperRef.current.zoom + "px sans-serif";
        const refPoint = sharedPointMapperRef.current.worldToScreen(pos);
        if (strokeOptions.fillColor) {
            this.ctx.fillText(text, refPoint.x, refPoint.y);
            this.ctx.strokeText(text, refPoint.x, refPoint.y);
        }
        else {
            this.ctx.strokeText(text, refPoint.x, refPoint.y);
        }
    }
}

export const sharedEditorCanvas = { current: new EditorCanvas(false) };
export const sharedRubberCanvas = { current: new EditorCanvas(true) };
export const sharedPointMapperRef = { current: new PointMapper() };

//todo: move to somewhere else
export const sharedEditedHotspot: { current: Hotspot2d | undefined } = { current: undefined };